import { Component, OnInit, Output, EventEmitter, Input, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { PantallasCustomizablesService, ICampoCustomizableRequest, ICampoCustomizableResponse, INuevoCampoSaveRequest } from '../../services/pantallas-customizables.service';
import { Subject, takeUntil } from 'rxjs';

interface ICampoOption {
  descripcion: string;
  caracteristica: string;
  itipo: number;
}

interface ICampoCustomizable {
  orden: number;
  campo: string;
  label: string;
}

@Component({
  selector: 'app-nuevo-campo-modal',
  templateUrl: './nuevo-campo-modal.component.html',
  providers: [MessageService]
})
export class NuevoCampoModalComponent implements OnInit, OnDestroy, OnChanges {
  @Input() sClave: string = '';
  @Input() campoParaEditar: ICampoCustomizable | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string; data?: any }>();

  form: FormGroup;
  loading: boolean = false;
  camposOptions: ICampoOption[] = [];
  isEditMode: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private pantallasCustomizablesService: PantallasCustomizablesService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadCamposOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campoParaEditar'] && changes['campoParaEditar'].currentValue) {
      this.isEditMode = true;
      this.initializeFormForEdit();
    } else {
      this.isEditMode = false;
      this.resetForm();
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      orden: ['', [Validators.required, Validators.min(1)]],
      campo: ['', [Validators.required]],
      etiqueta: ['', [Validators.required]]
    });
  }

  initializeFormForEdit(): void {
    if (this.campoParaEditar) {
      this.form.patchValue({
        orden: this.campoParaEditar.orden,
        campo: this.campoParaEditar.campo,
        etiqueta: this.campoParaEditar.label
      });
      
      // En modo edición, deshabilitar Orden y Campo
      this.form.get('orden')?.disable();
      this.form.get('campo')?.disable();
    }
  }

  resetForm(): void {
    this.form.reset();
    this.form.markAsUntouched();
    this.form.markAsPristine();
    
    // Habilitar todos los campos al resetear
    this.form.get('orden')?.enable();
    this.form.get('campo')?.enable();
    this.form.get('etiqueta')?.enable();
  }

  loadCamposOptions(): void {
    const request: ICampoCustomizableRequest = {
      iAccion: 0,
      sCampo: "",
      sCaract: "",
      iNumReg: 9999
    };

    this.pantallasCustomizablesService.getCamposCustomizables(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ICampoCustomizableResponse) => {
          if (response.success) {
            this.camposOptions = response.data;
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudieron cargar las opciones de campos'
            });
          }
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar las opciones de campos'
          });
          console.error('Error:', error);
        }
      });
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.loading = true;
      
      // Habilitar los campos temporalmente para obtener sus valores
      const ordenControl = this.form.get('orden');
      const campoControl = this.form.get('campo');
      
      if (ordenControl?.disabled) {
        ordenControl.enable();
      }
      if (campoControl?.disabled) {
        campoControl.enable();
      }

      const formData = this.form.value;
      const request: INuevoCampoSaveRequest = {
        iAccion: this.isEditMode ? 2 : 1, // 2 para actualizar, 1 para crear
        sClave: this.sClave,
        iOrden: formData.orden,
        sCampo: formData.campo,
        sEtiqueta: formData.etiqueta,
        iObliga: 0
      };

      this.pantallasCustomizablesService.saveNuevoCampo(request)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const errorCode = response.data?.[0]?.error;
            if (errorCode === "0") {
              this.onSave.emit({
                success: true,
                message: this.isEditMode ? 'Campo actualizado correctamente' : 'Campo agregado correctamente',
                data: formData
              });
              this.resetForm(); // Limpiar formulario en caso de éxito
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: errorCode || (this.isEditMode ? 'Error al actualizar el campo' : 'Error al agregar el campo')
              });
              this.resetForm(); // Limpiar formulario en caso de error
            }
          },
          error: (error) => {
            console.error('Error:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.isEditMode ? 'Error al actualizar el campo' : 'Error al agregar el campo'
            });
            this.resetForm(); // Limpiar formulario en caso de error de red
          },
          complete: () => {
            this.loading = false;
          }
        });
    } else {
      this.markFormGroupTouched();
    }
  }

  onCancel(): void {
    this.resetForm(); // Limpiar formulario al cancelar
    this.onSave.emit({ success: false, message: '' });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) {
        return 'Este campo es requerido';
      }
      if (field.errors['min']) {
        return 'El valor mínimo es 1';
      }
    }
    return '';
  }
} 